"use client"

import { useEffect, useState } from "react"
import { useParams } from "next/navigation"
import { Star, Heart, ShoppingCart, Minus, Plus, Shield, Truck, RotateCcw, ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import Header from "@/components/header"
import { useCart } from "@/hooks/use-cart"
import { fetchProductById, getImageUrl, calculateDiscountedPrice, isDiscountActive } from "@/lib/utils"
import DiscountBadge from "@/components/discount-badge"

export default function ProductDetailPage() {
  const params = useParams()
  const productId = params.id as string
  const { addItem, state } = useCart()

  const [product, setProduct] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [activeTab, setActiveTab] = useState("description")

  useEffect(() => {
    setLoading(true)
    setError(null)
    fetchProductById(productId)
      .then((data) => {
        // Map API fields to frontend fields
        const originalPrice = Number(data.selling_price || data.price || 0)
        const { discountedPrice, discountAmount } = calculateDiscountedPrice(originalPrice)
        const isDiscountApplied = isDiscountActive() && discountAmount > 0
        
        const mappedProduct = {
          id: data.id,
          name: data.product_name || data.name || data.code || 'Product',
          price: isDiscountApplied ? discountedPrice : originalPrice,
          originalPrice: isDiscountApplied ? originalPrice : (data.compare_price ? Number(data.compare_price) : undefined),
          rating: data.rating ?? 0,
          reviews: data.reviews ?? 0,
          image: getImageUrl(data.image || (data.images && data.images.length > 0 ? data.images[0].image : undefined)) || "/banners/no-img.jpg",
          images: (() => {
            const processedImages = data.images 
              ? data.images.map((img: any) => {
                  const url = getImageUrl(img.image);
                  return url && url !== "/banners/no-img.jpg" ? url : null;
                }).filter(Boolean)
              : data.image 
                ? [getImageUrl(data.image)].filter(url => url && url !== "/banners/no-img.jpg")
                : [];
            return processedImages.length > 0 ? processedImages : ["/banners/no-img.jpg"];
          })(),
          category: data.category || '',
          brand: data.brand || '',
          condition: data.condition || '',
          inStock: data.in_stock ?? (data.stock_available > 0) ?? true,
          discount: data.discount ?? 0,
          description: data.description || data.short_description || '',
          features: data.features || [],
          ingredients: data.ingredients || '',
          dosage: data.dosage || '',
          warnings: data.warnings || '',
          stockCount: data.stock_available ?? 0,
          packSize: data.pack_size || '',
          barcode: data.barcode || '',
          prescriptionOnly: data.prescription_only || false,
          isVatable: data.is_vatable || false,
          isDiscounted: data.is_discounted || false,
          ...data,
        }
        console.log('Mapped product images:', mappedProduct.images)
        setProduct(mappedProduct)
        setLoading(false)
      })
      .catch((err) => {
        setError("Failed to load product.")
        setLoading(false)
      })
  }, [productId])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Header />
        <div className="text-center text-lg text-gray-500">Loading product...</div>
      </div>
    )
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Header />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <p className="text-gray-600">The product you're looking for doesn't exist.</p>
        </div>
      </div>
    )
  }

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.images && product.images.length > 0 ? product.images[0] : product.image,
      inStock: product.inStock,
      is_vatable: product.isVatable,
    })
  }

  const addToWishlist = () => {
    console.log(`Adding product ${product.id} to wishlist`)
    // Add to wishlist logic here
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-gray-600 mb-8">
          <span>Home</span> / <span>Shop</span> / <span>{product.category}</span> /{" "}
          <span className="text-gray-900">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Product Images */}
          <div>
            <div className="mb-4 relative">
              {/* <Image
                src={product.images[selectedImage] || product.images[0] || "/placeholder.svg"}
                alt={product.name}
                width={500}
                height={500}
                className="w-full h-96 object-cover rounded-lg border"
              /> */}
              <img 
                src={product.image} 
                alt={product.name} 
                className="w-full h-96 object-cover rounded-lg border"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/banners/no-img.jpg";
                }}
              />
              <DiscountBadge />
            </div>
            {(product.images && product.images.length > 0 && product.images.some((img: string) => img && img !== "/banners/no-img.jpg")) && (
              <div className="flex gap-2">
                {product.images
                  .filter((image: string) => image && image !== "" && image !== "/banners/no-img.jpg")
                  .map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 border-2 rounded-lg overflow-hidden ${
                        selectedImage === index ? "border-green-600" : "border-gray-200"
                      }`}
                    >
                      {/* {product.image} */}
                      <img 
                        src={product.image} 
                        alt={`${product.name} ${index + 1}`} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "/banners/no-img.jpg";
                        }}
                      />
                      {/* <Image
                        src={image}
                        alt={`${product.name} ${index + 1}`}
                        width={80}
                        height={80}
                        className="w-full h-full object-cover"
                      /> */}
                    </button>
                  ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="text-sm text-primary mb-2">{product.category}</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>

            <div className="flex items-center gap-4 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.floor(product.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600">({product.reviews} reviews)</span>
              <span className="text-sm text-gray-600">Brand: {product.brand}</span>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <span className="text-3xl font-bold text-green-600">KSh {product.price.toLocaleString()}</span>
              {product.originalPrice && (
                <span className="text-xl text-gray-500 line-through">
                  {product.originalPrice > product.price && (
                    <span>
                      KSh {product.originalPrice.toLocaleString()}
                    </span>
                  )}
                </span>
              )}
              {product.discount && (
                <span className="bg-red-500 text-white text-sm px-2 py-1 rounded">-{product.discount}% OFF</span>
              )}
            </div>

            <p className="text-gray-700 mb-6">{product.description}</p>

            {/* Stock Status */}
            <div className="mb-6">
              {product.inStock ? (
                <div className="flex items-center gap-2 text-green-600">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span>In Stock ({product.stockCount} available)</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-600">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  <span>Out of Stock</span>
                </div>
              )}
              {product.is_vatable && (
                <div className="flex items-center gap-2 text-red-600">
                  <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                  <span>VAT Exclusive</span>
                </div>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 mb-6">
              <span className="font-medium">Quantity:</span>
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 hover:bg-gray-100">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 py-2 border-x border-gray-300">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-2 hover:bg-gray-100">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 mb-8">
              <button
                onClick={handleAddToCart}
                disabled={!product.inStock || state.isLoading}
                className="flex-1 bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center justify-center gap-2"
              >
                {state.isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ShoppingCart className="w-5 h-5" />
                )}
                {state.isLoading ? "Adding..." : "Add To Basket"}
              </button>
              <button onClick={addToWishlist} className="border border-gray-300 p-3 rounded-lg hover:bg-gray-50">
                <Heart className="w-5 h-5" />
              </button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-600">Genuine Product</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-600" />
                <span className="text-sm text-gray-600">Fast Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-600">Easy Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="border-t border-gray-200">
          <div className="flex border-b border-gray-200">
            {["description"].map((tab) => (
            // {["description", "ingredients", "dosage", "warnings"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-medium capitalize ${
                  activeTab === tab
                    ? "text-green-600 border-b-2 border-green-600"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="py-8">
            {activeTab === "description" && (
              <div>
                <h3 className="text-xl font-semibold mb-4">Product Description</h3>
                <p className="text-gray-700 mb-6">{product.short_description}</p>
                <p className="text-gray-700 mb-6">{product.description}</p>
              </div>
            )}
{/* 
            {activeTab === "ingredients" && (
              <div>
                <h3 className="text-xl font-semibold mb-4">Ingredients</h3>
                <p className="text-gray-700">{product.ingredients}</p>
              </div>
            )}

            {activeTab === "dosage" && (
              <div>
                <h3 className="text-xl font-semibold mb-4">Dosage & Administration</h3>
                <p className="text-gray-700">{product.dosage}</p>
              </div>
            )}

            {activeTab === "warnings" && (
              <div>
                <h3 className="text-xl font-semibold mb-4">Warnings & Precautions</h3>
                <p className="text-gray-700">{product.warnings}</p>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </div>
  )
}
